/**
 * Premortem Letter G — Sandata EVV mode visibility.
 *
 * Exports two related components:
 *   - <SandataModeBadge />: a tiny header pill (sits next to OdmEnvBadge)
 *     that shows EVV: MOCK / CERT / PROD with a severity-tinted color.
 *   - <MockedEvvBanner />: a full-width warning banner intended to render
 *     at the top of every EVV-related page (EVVPage, EVVResolutionPage,
 *     SandataCertPage) so caregivers/admins can't miss the "this is not
 *     real" warning.
 *
 * Both consume the same /api/evv/mode-status endpoint, share a tiny
 * shared fetch hook, and re-poll every 60s so a credential flip in
 * another tab updates them automatically.
 *
 * Why this exists: prior to Letter G, the EVV pages showed identical
 * success states for real CERT submissions and for mock /tmp writes.
 * A pilot agency could go weeks thinking they were EVV-compliant while
 * none of their visits had reached the aggregator, blowing past their
 * 14-day timely-submission window. This is the visible, in-your-face
 * signal that prevents that class of misunderstanding.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Warning, WarningOctagon } from '@phosphor-icons/react';
import api from '../services/api';

const POLL_INTERVAL_MS = 60_000;

/** Tiny shared fetcher — both the badge and the banner subscribe to the
 * same poll so we don't double the HTTP load. The hook caches in module
 * scope (not React state) so a remount doesn't re-fetch immediately. */
let _cachedStatus = null;
let _cachedStatusAt = 0;

const useSandataModeStatus = () => {
  const [status, setStatus] = useState(_cachedStatus);
  const [loading, setLoading] = useState(!_cachedStatus);

  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      api.get('/evv/mode-status')
        .then((res) => {
          if (cancelled) return;
          _cachedStatus = res.data;
          _cachedStatusAt = Date.now();
          setStatus(res.data);
        })
        .catch(() => { /* silent — banner just won't render */ })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    // Refresh if the cache is older than the poll interval.
    if (!_cachedStatus || (Date.now() - _cachedStatusAt) > POLL_INTERVAL_MS) {
      refresh();
    }
    const t = setInterval(refresh, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return { status, loading };
};

// --------------------------------------------------------------------
// <SandataModeBadge /> — global header pill
// --------------------------------------------------------------------
export const SandataModeBadge = () => {
  const { status } = useSandataModeStatus();
  if (!status) return null;

  const { is_mock, is_prod, severity, api: apiInfo, sftp: sftpInfo } = status;

  const styles =
    severity === 'critical'
      ? 'bg-rose-50 text-rose-800 border-rose-300 hover:bg-rose-100'
      : severity === 'warning'
        ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
        : 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100';

  const Icon = severity === 'critical' ? WarningOctagon : severity === 'warning' ? Warning : ShieldCheck;

  // Compact label — show what we know about each transport.
  let label;
  if (is_prod) {
    label = 'EVV: PROD';
  } else if (is_mock) {
    label = 'EVV: MOCK';
  } else {
    label = `EVV: ${(apiInfo.mode || 'CERT').toUpperCase()}`;
  }

  const tooltip =
    `Sandata API: ${apiInfo.mode}${apiInfo.configured ? '' : ' (not configured)'} · ` +
    `SFTP: ${sftpInfo.mode}${sftpInfo.configured ? ` (${sftpInfo.environment})` : ' (not configured)'}` +
    `\nClick to manage EVV credentials.`;

  return (
    <Link
      to="/integrations"
      title={tooltip}
      className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-semibold transition-colors mr-2 ${styles}`}
      data-testid="sandata-mode-badge"
    >
      <Icon size={14} weight={severity === 'ok' ? 'fill' : 'bold'} />
      {label}
    </Link>
  );
};

// --------------------------------------------------------------------
// <MockedEvvBanner /> — full-width warning at the top of each EVV page
// --------------------------------------------------------------------
export const MockedEvvBanner = ({ context }) => {
  const { status } = useSandataModeStatus();
  if (!status || status.is_prod || (!status.is_mock && status.severity === 'ok')) {
    // Hide on prod + on clean state. Show on warning AND on critical so
    // CERT/UAT users also see the "not for production" disclaimer.
    return null;
  }

  const isCritical = status.severity === 'critical';
  const wrapperStyles = isCritical
    ? 'bg-rose-50 border-rose-300 text-rose-900'
    : 'bg-amber-50 border-amber-300 text-amber-900';
  const Icon = isCritical ? WarningOctagon : Warning;

  // Per-context guidance helps each page surface the SPECIFIC implication.
  // EVV record page → "visits won't reach Sandata"
  // Resolution page → "approvals won't trigger real submission"
  // Cert tests page → "this is testing the real CERT, not mock"
  const contextHint = {
    'evv-record': 'Newly recorded visits below show the same statuses they would in production, but the underlying Sandata transport is not live — see the credentials at /integrations.',
    'evv-resolution': 'Approving and submitting entries from this page does NOT push them to the real Sandata aggregator while mode is mock. Use this for workflow testing only.',
    'sandata-cert': 'These cert-scenario runs hit the real Sandata UAT environment; mock mode below means no SFTP file actually leaves the box.',
  }[context] || 'Visits and verifications surfaced on this page reflect MOCK transport status until Sandata SFTP credentials are configured in the backend .env.';

  return (
    <div
      className={`rounded-lg border p-4 flex items-start gap-3 ${wrapperStyles}`}
      data-testid="mocked-evv-banner"
      role="alert"
    >
      <Icon size={22} weight="fill" className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-sm">
        <div className="font-semibold text-base mb-1">
          {isCritical ? 'EVV transports running in MOCK mode' : 'EVV is NOT in production mode'}
        </div>
        <div className="mb-2">{status.summary}</div>
        <div className="text-xs opacity-90">{contextHint}</div>
        <div className="mt-3 flex gap-4 text-xs font-mono">
          <span>
            <span className="opacity-70">API:</span>{' '}
            <span className={status.api.mode === 'mock' ? 'text-rose-700 font-bold' : ''}>
              {status.api.mode.toUpperCase()}
              {!status.api.configured && ' (unconfigured)'}
            </span>
          </span>
          <span>
            <span className="opacity-70">SFTP:</span>{' '}
            <span className={status.sftp.mode === 'mock' ? 'text-rose-700 font-bold' : ''}>
              {status.sftp.mode.toUpperCase()}
              {status.sftp.configured && ` (${status.sftp.environment})`}
            </span>
          </span>
        </div>
      </div>
      <Link
        to="/integrations"
        className={`text-xs font-semibold underline whitespace-nowrap ${isCritical ? 'text-rose-700' : 'text-amber-800'}`}
        data-testid="mocked-evv-banner-configure-link"
      >
        Configure →
      </Link>
    </div>
  );
};

export default SandataModeBadge;
